// Booking analytics workspace — /analytics/bookings. Answers "how busy is this
// facility — volume, utilization, when it peaks — over the selected window?"
//
// THIN presentation layer: every number is a backend field, rendered AS-IS.
// There is NO client-side math here (the heatmap's colour ramp is the only
// derived signal, and it is a pure visual mapping, not a reported number).
//
// Two recreated-bug traps this page guards (see the RED suite):
//   1. utilization_percent is ALREADY 0–100 — render it directly. NEVER multiply
//      by 100, and NEVER pass it through @bw/shared/utils formatPercent (which
//      scales a 0–1 fraction → would print "4250%").
//   2. The per-hour peak count field is `booking_count`, NOT `count`. The Peak
//      Hour is argmax over peak_hours[].booking_count; reading `.count` would be
//      undefined and the peak would vanish.
//
// Backing data (engineer-added clients in libs/analyticsApi.ts):
//   getFacilityBookingCount(id, start, end) -> { booking_count }
//   getFacilityUtilization(id, start, end)  -> { utilization_percent }  (0–100)
//   getFacilityPeakHours(id, start, end)    -> { peak_hours: [{hour,booking_count}] }
//   getFacilityTrends(id, period)           -> { data_points: [...] }
//   getFacilityOccupancy(id, date)          -> OccupancyRow[]
//
// Conventions mirror operator-dashboard.tsx: useResolvedFacility gate (#883),
// plain useEffect + useState + Promise.allSettled (no react-query for facility
// data). NO Cancellation-Rate / Court-Popularity / Booking-Type / No-Show tiles
// (no backend → they must not appear at all).

import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useEffect, useMemo, useState, type ElementType } from "react";
import {
  getFacilityBookingCount,
  getFacilityUtilization,
  getFacilityPeakHours,
  getFacilityTrends,
  getFacilityOccupancy,
  type FacilityBookingCountResponse,
  type FacilityUtilizationResponse,
  type FacilityPeakHoursResponse,
  type FacilityTrendsResponse,
} from "@/libs/analyticsApi";
import { useResolvedFacility } from "@/hooks/use-selected-facility";
import { PeriodSelector, type AnalyticsPeriod } from "./period-selector";
import { rangeForPeriod } from "./period-range";
import { finiteOrNull } from "./finite-field";
import { RevenueBarChart, type RevenueBarPoint } from "./revenue-bar-chart";
import { UtilizationHeatmap, type HeatmapDay } from "./utilization-heatmap";

const PLACEHOLDER = "—";

// The heatmap fetches one occupancy call PER DAY, so the window is capped at a
// fixed 7 days regardless of the selected period (a "month" would otherwise fire
// 30 occupancy reads). The KPIs + bars still cover the full selected period;
// only the day×hour heatmap uses this bounded recent window.
const HEATMAP_DAYS = 7;

const numberFormatter = new Intl.NumberFormat("en-US");

// 12h hour-of-day label (e.g. "6 PM", "12 PM").
function hourLabel(hour: number): string {
  const period = hour < 12 ? "AM" : "PM";
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display} ${period}`;
}

// Short day label (e.g. "Jun 8") from an ISO date, pinned to local midnight.
function dayLabel(date: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
  }).format(new Date(`${date}T00:00:00`));
}

// Local-calendar YYYY-MM-DD for `now` minus `back` days (no UTC shift).
function localISODateBack(now: Date, back: number): string {
  const d = new Date(now);
  d.setDate(d.getDate() - back);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function Kpi({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: ElementType;
}) {
  return (
    <Paper sx={{ p: 1.5 }} variant="outlined">
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Box
          sx={{
            bgcolor: "action.selected",
            borderRadius: 2,
            color: "primary.main",
            display: "grid",
            flexShrink: 0,
            height: 40,
            placeItems: "center",
            width: 40,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{ fontVariantNumeric: "tabular-nums", fontWeight: 700 }}
            variant="h6"
          >
            {value}
          </Typography>
          <Typography color="text.secondary" variant="caption">
            {label}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export function BookingAnalyticsWorkspace() {
  const theme = useTheme();
  const {
    facility,
    facilityId,
    isLoading: facilityLoading,
  } = useResolvedFacility();

  const [period, setPeriod] = useState<AnalyticsPeriod>("week");

  const [bookingCount, setBookingCount] =
    useState<FacilityBookingCountResponse | null>(null);
  const [utilization, setUtilization] =
    useState<FacilityUtilizationResponse | null>(null);
  const [peakHours, setPeakHours] = useState<FacilityPeakHoursResponse | null>(
    null,
  );
  const [trends, setTrends] = useState<FacilityTrendsResponse | null>(null);
  const [heatmap, setHeatmap] = useState<HeatmapDay[]>([]);
  const [loading, setLoading] = useState(true);
  // A failed fetch must NEVER fabricate a count / percentage — gate into an
  // honest "—" / banner via these error flags.
  const [statsError, setStatsError] = useState<string | null>(null);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);

  useEffect(() => {
    // Gate every read on a truthy, org-verified facilityId (#883). No facility
    // resolved yet → ZERO api calls.
    if (!facilityId) return;
    let cancelled = false;
    const { start, end } = rangeForPeriod(period);
    const now = new Date();
    setLoading(true);
    setStatsError(null);
    setHeatmapError(null);
    void (async () => {
      // The bounded recent days the heatmap renders (oldest → newest), each a
      // separate occupancy read. Capped at HEATMAP_DAYS regardless of period.
      const heatmapDates = Array.from({ length: HEATMAP_DAYS }, (_, i) =>
        localISODateBack(now, HEATMAP_DAYS - 1 - i),
      );

      const [
        countResult,
        utilResult,
        peakResult,
        trendsResult,
        ...occupancyResults
      ] = await Promise.allSettled([
        getFacilityBookingCount(facilityId, start, end),
        getFacilityUtilization(facilityId, start, end),
        getFacilityPeakHours(facilityId, start, end),
        getFacilityTrends(facilityId, period, end),
        ...heatmapDates.map((date) => getFacilityOccupancy(facilityId, date)),
      ]);
      if (cancelled) return;

      setBookingCount(
        countResult.status === "fulfilled" ? countResult.value : null,
      );
      setUtilization(
        utilResult.status === "fulfilled" ? utilResult.value : null,
      );
      setPeakHours(peakResult.status === "fulfilled" ? peakResult.value : null);
      setTrends(
        trendsResult.status === "fulfilled" ? trendsResult.value : null,
      );

      // The four KPI/trend reads are one "stats" surface; if the core ones
      // failed, show a single honest banner (never invent a number).
      if (
        countResult.status === "rejected" &&
        utilResult.status === "rejected" &&
        peakResult.status === "rejected"
      ) {
        const reason = countResult.reason;
        setStatsError(
          reason instanceof Error ? reason.message : "Couldn't load analytics.",
        );
      }

      // Heatmap: keep only the days that resolved; an all-failed window shows an
      // honest banner rather than a fabricated grid.
      const days: HeatmapDay[] = [];
      occupancyResults.forEach((res, i) => {
        if (res.status === "fulfilled") {
          days.push({ date: heatmapDates[i], rows: res.value });
        }
      });
      setHeatmap(days);
      if (days.length === 0) {
        const failed = occupancyResults.find((r) => r.status === "rejected");
        if (failed && failed.status === "rejected") {
          setHeatmapError(
            failed.reason instanceof Error
              ? failed.reason.message
              : "Couldn't load occupancy.",
          );
        }
      }

      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [facilityId, period]);

  // Daily bookings bars — one per backend trend point. booking_count is
  // unenforced (#1340): drop any point whose count is not a finite number rather
  // than plot a fabricated bar (a non-finite value would render as a 0/NaN bar).
  const barData = useMemo<RevenueBarPoint[]>(() => {
    const points: RevenueBarPoint[] = [];
    for (const p of trends?.data_points ?? []) {
      const value = finiteOrNull(p?.booking_count);
      if (value === null) continue;
      points.push({ label: dayLabel(p.date), value });
    }
    return points;
  }, [trends?.data_points]);

  // Peak Hour = argmax over peak_hours[].booking_count (NOT `.count`). Each
  // bucket's `hour` and `booking_count` are unenforced (#1340), so a bucket
  // counts ONLY if BOTH are finite — a malformed winning bucket (missing/null
  // hour, or a non-numeric count) must NOT fabricate a "NaN PM" / "12 AM" peak.
  // null when no VALID bucket exists, so the KPI degrades to an honest "—".
  const peakHour = useMemo<{
    hour: number;
    bookingCount: number;
  } | null>(() => {
    let best: { hour: number; bookingCount: number } | null = null;
    for (const b of peakHours?.peak_hours ?? []) {
      const hour = finiteOrNull(b?.hour);
      const count = finiteOrNull(b?.booking_count);
      if (hour === null || count === null) continue;
      if (best === null || count > best.bookingCount) {
        best = { hour, bookingCount: count };
      }
    }
    return best;
  }, [peakHours?.peak_hours]);

  // Brief resolve window before the FacilityContext selection lands.
  if (facilityLoading || !facilityId) {
    return (
      <Stack
        aria-label="Loading booking analytics"
        role="status"
        spacing={1}
        sx={{
          alignItems: "center",
          justifyContent: "center",
          minHeight: "40vh",
        }}
      >
        <CircularProgress aria-hidden size={24} />
        <Typography color="text.secondary" variant="body2">
          Loading…
        </Typography>
      </Stack>
    );
  }

  // Total Bookings — booking_count is unenforced (#1340), so gate it to a finite
  // number BEFORE formatting; a null/absent/non-numeric count must degrade to "—"
  // rather than leak "NaN" via Intl.NumberFormat.
  const bookingCountValue = finiteOrNull(bookingCount?.booking_count);
  const totalBookings =
    bookingCountValue !== null
      ? numberFormatter.format(bookingCountValue)
      : PLACEHOLDER;

  // Avg Utilization — rendered AS-IS (already 0–100). NEVER ×100, NEVER
  // formatPercent. utilization_percent is unenforced (#1340): a null/absent value
  // must NOT fabricate "0%" (reads as a truthful empty facility) or "NaN%". Gate
  // to a finite number BEFORE rounding; else an honest "—".
  const utilizationValue = finiteOrNull(utilization?.utilization_percent);
  const avgUtilization =
    utilizationValue !== null
      ? `${Math.round(utilizationValue)}%`
      : PLACEHOLDER;

  const peakHourValue =
    peakHour !== null
      ? `${hourLabel(peakHour.hour)} (${peakHour.bookingCount})`
      : PLACEHOLDER;

  return (
    <Stack spacing={2.5} sx={{ px: { xs: 2, lg: 3 }, pb: 2 }}>
      {/* The app shell supplies the "Bookings" page title, so the content does
          NOT repeat it (ui-quality.md). The in-content heading is the facility
          name; the only "Bookings"-bearing label below is the Total Bookings
          KPI — one source of truth per metric. */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        sx={{
          alignItems: { xs: "stretch", sm: "center" },
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography component="h1" sx={{ fontWeight: 700 }} variant="h5">
            {facility?.name ?? "Your facility"}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Volume and utilization for the selected window
          </Typography>
        </Box>
        <PeriodSelector onChange={setPeriod} value={period} />
      </Stack>

      {statsError ? (
        <Alert severity="error">
          We couldn&apos;t load booking analytics. {statsError}
        </Alert>
      ) : null}

      {/* KPI strip — every value a backend field, rendered AS-IS. */}
      <Box
        sx={{
          display: "grid",
          gap: 1.5,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" },
        }}
      >
        <Kpi
          icon={BarChartOutlinedIcon}
          label="Total Bookings"
          value={loading ? PLACEHOLDER : totalBookings}
        />
        <Kpi
          icon={PercentOutlinedIcon}
          label="Avg Utilization"
          value={loading ? PLACEHOLDER : avgUtilization}
        />
        <Kpi
          icon={ScheduleOutlinedIcon}
          label="Peak Hour"
          value={loading ? PLACEHOLDER : peakHourValue}
        />
      </Box>

      {/* Daily booking-volume bar chart from getFacilityTrends. Titled "Daily
          volume" (not "…bookings") so the only "Bookings"-bearing label on the
          page is the Total Bookings KPI. */}
      <Paper component="section" sx={{ overflow: "hidden" }} variant="outlined">
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
          <Typography component="h2" variant="h6">
            Daily volume
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
          {loading ? (
            <Stack
              role="status"
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                minHeight: 192,
              }}
            >
              <CircularProgress aria-hidden size={24} />
              <Typography color="text.secondary" variant="body2">
                Loading…
              </Typography>
            </Stack>
          ) : barData.length === 0 ? (
            <Stack
              sx={{
                alignItems: "center",
                justifyContent: "center",
                minHeight: 192,
              }}
            >
              <Typography color="text.secondary" variant="body2">
                No sessions in this window.
              </Typography>
            </Stack>
          ) : (
            <RevenueBarChart
              color={theme.palette.secondary.main}
              data={barData}
              formatValue={(v) => numberFormatter.format(v)}
              seriesLabel="Sessions"
            />
          )}
        </Box>
      </Paper>

      {/* Utilization heatmap (day × hour) from getFacilityOccupancy — a bounded
          recent 7-day window. */}
      <Paper component="section" sx={{ overflow: "hidden" }} variant="outlined">
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2 }}>
          <Typography component="h2" variant="h6">
            Court utilization
          </Typography>
        </Box>
        <Divider />
        <Box sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
          {heatmapError ? (
            <Alert severity="error">
              We couldn&apos;t load occupancy. {heatmapError}
            </Alert>
          ) : loading ? (
            <Stack
              role="status"
              spacing={1}
              sx={{
                alignItems: "center",
                justifyContent: "center",
                minHeight: 128,
              }}
            >
              <CircularProgress aria-hidden size={24} />
              <Typography color="text.secondary" variant="body2">
                Loading…
              </Typography>
            </Stack>
          ) : (
            <UtilizationHeatmap days={heatmap} />
          )}
        </Box>
      </Paper>
    </Stack>
  );
}
